import { parseValueBRL } from './formatters';

/**
 * Configuração padrão de combustível usada pela Jornada Pro.
 *
 * Modelo elétrico:
 * - pricePerKwh: valor pago por kWh (R$/kWh)
 * - consumptionKwh100: consumo médio em kWh a cada 100 km.
 *   Quanto MENOR o valor, mais econômico é o veículo.
 */
export const DEFAULT_FUEL_CONFIG = {
    type: 'liquid',
    price: '5,50',
    consumption: '12,0',
    pricePerKwh: '0,80',
    consumptionKwh100: '15,0',
    isHybridPlugin: false,
    hybridMode: 'mixed'
};

const DEFAULT_PRICE_PER_KWH = 0.80;
const DEFAULT_CONSUMPTION_KWH_100 = 15;
const DEFAULT_LIQUID_KM_COST = 0.45;

const toNumber = (value) => {
    const parsed = parseValueBRL(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const toInputValue = (value, fallback, decimals) => {
    const parsed = toNumber(value);
    const safe = parsed > 0 ? parsed : fallback;
    return safe.toFixed(decimals).replace('.', ',');
};

/**
 * Normaliza uma configuração salva, incluindo migração automática do modelo
 * antigo (Carga Total + Autonomia) para o modelo atual (R$/kWh + kWh/100 km).
 *
 * A migração preserva o custo por km antigo, assumindo o valor padrão de
 * R$ 0,80/kWh quando o usuário ainda não informou o preço do kWh.
 */
export const normalizeFuelConfig = (saved) => {
    if (!saved || typeof saved !== 'object') {
        return { ...DEFAULT_FUEL_CONFIG };
    }

    const merged = { ...DEFAULT_FUEL_CONFIG, ...saved };
    const hasNewElectricModel = saved.pricePerKwh !== undefined && saved.consumptionKwh100 !== undefined;

    if (!hasNewElectricModel) {
        const oldTotalCharge = toNumber(saved.priceElectric);
        const oldAutonomyKm = toNumber(saved.consumptionElectric);

        if (oldTotalCharge > 0 && oldAutonomyKm > 0) {
            const oldCostPerKm = oldTotalCharge / oldAutonomyKm;
            const migratedConsumption = (oldCostPerKm / DEFAULT_PRICE_PER_KWH) * 100;

            // Evita migrar valores irrealistas (ex.: configs antigas corrompidas)
            if (migratedConsumption > 0 && migratedConsumption < 200) {
                merged.pricePerKwh = DEFAULT_PRICE_PER_KWH.toFixed(2).replace('.', ',');
                merged.consumptionKwh100 = migratedConsumption.toFixed(1).replace('.', ',');
            }
        }
    }

    merged.pricePerKwh = toInputValue(merged.pricePerKwh, DEFAULT_PRICE_PER_KWH, 2);
    merged.consumptionKwh100 = toInputValue(merged.consumptionKwh100, DEFAULT_CONSUMPTION_KWH_100, 1);
    merged.price = toInputValue(merged.price, 5.50, 2);
    merged.consumption = toInputValue(merged.consumption, 12, 1);
    merged.isHybridPlugin = Boolean(merged.isHybridPlugin);
    merged.hybridMode = ['liquid', 'electric', 'mixed'].includes(merged.hybridMode)
        ? merged.hybridMode
        : 'mixed';
    merged.type = ['liquid', 'gnv', 'electric'].includes(merged.type) ? merged.type : 'liquid';

    return merged;
};

/**
 * Custo estimado por km (R$/km) de acordo com o tipo de combustível.
 * Elétrico: (R$/kWh * kWh/100km) / 100.
 */
export const getFuelKmCost = (config) => {
    const c = normalizeFuelConfig(config);

    const liquidPrice = toNumber(c.price);
    const liquidConsumption = toNumber(c.consumption);
    const kwhPrice = toNumber(c.pricePerKwh);
    const kwhPer100Km = toNumber(c.consumptionKwh100);

    const liquidCostPerKm = liquidConsumption > 0
        ? liquidPrice / liquidConsumption
        : DEFAULT_LIQUID_KM_COST;

    const electricCostPerKm = kwhPrice > 0 && kwhPer100Km > 0
        ? (kwhPrice * kwhPer100Km) / 100
        : 0;

    if (c.type === 'electric' && c.isHybridPlugin) {
        if (c.hybridMode === 'liquid') return liquidCostPerKm;
        if (c.hybridMode === 'electric') return electricCostPerKm;
        // Misto: estimativa 50/50 entre os dois modos.
        return (liquidCostPerKm * 0.5) + (electricCostPerKm * 0.5);
    }

    if (c.type === 'electric') return electricCostPerKm;

    return liquidCostPerKm;
};

/**
 * Texto curto para exibir no botão de configuração de combustível.
 */
export const getFuelConfigLabel = (config) => {
    const c = normalizeFuelConfig(config);

    if (c.type === 'electric' && c.isHybridPlugin) {
        if (c.hybridMode === 'liquid') {
            return {
                title: `HB Líquido • ${c.consumption} km/L`,
                value: `R$ ${c.price}`
            };
        }
        if (c.hybridMode === 'electric') {
            return {
                title: `HB Elétrico • ${c.consumptionKwh100} kWh/100km`,
                value: `R$ ${c.pricePerKwh}/kWh`
            };
        }
        return {
            title: 'HB Misto',
            value: `R$ ${getFuelKmCost(c).toFixed(2)}/km`
        };
    }

    if (c.type === 'electric') {
        return {
            title: `EV • ${c.consumptionKwh100} kWh/100km`,
            value: `R$ ${c.pricePerKwh}/kWh`
        };
    }

    return {
        title: `${c.type === 'gnv' ? 'GNV' : 'Média'} • ${c.consumption}${c.type === 'gnv' ? 'm³' : 'km/L'}`,
        value: `R$ ${c.price}`
    };
};
